/**
 * 金额运算 —— 全部整数,单位是货币的最小单位(分 / 仙 / cent)。
 *
 * 禁止用 float 表示金额。0.1 + 0.2 !== 0.3 这个问题在账目上是致命的,
 * 而且历史数据一旦用 float 存了就无法安全转换。见 docs/13-v1-scope.md §13.4 第 6 条。
 */

/** 最小货币单位的整数金额。用 branded type 防止和普通 number 混用 */
export type Minor = number & { readonly __brand: 'Minor' };

export const ZERO = 0 as Minor;

export function minor(n: number): Minor {
  if (!Number.isInteger(n)) {
    throw new TypeError(`金额必须是整数(最小货币单位),收到 ${n}`);
  }
  if (!Number.isSafeInteger(n)) {
    throw new RangeError(`金额超出安全整数范围: ${n}`);
  }
  return n as Minor;
}

/** 从「元」转最小单位。仅用于解析用户输入,内部一律用 Minor */
export function fromMajor(major: number | string, decimals = 2): Minor {
  const s = typeof major === 'string' ? major.trim() : String(major);
  if (!/^-?\d+(\.\d+)?$/.test(s)) {
    throw new TypeError(`无效的金额格式: ${major}`);
  }
  const neg = s.startsWith('-');
  const [intPart = '0', fracPart = ''] = (neg ? s.slice(1) : s).split('.');
  const frac = fracPart.padEnd(decimals, '0').slice(0, decimals);
  // 第 decimals+1 位用于四舍五入
  const roundDigit = fracPart[decimals];
  let value = Number(intPart) * 10 ** decimals + Number(frac || '0');
  if (roundDigit && Number(roundDigit) >= 5) value += 1;
  return minor(neg ? -value : value);
}

/**
 * 宽松解析用户**正在敲**的金额。
 *
 * ★ `fromMajor` 对格式不合法的输入抛异常 —— 而输入框里一定会经过
 *   「敲了一半」的中间态:`.`、`120.`、`-`、空串。
 *   在 React 的 render 或 onChange 里抛,整棵树会被卸载 ——
 *   用户看到的是**页面自己刷新了一下,填的东西全没了**,
 *   而他做的只是敲了一个小数点。(这条是真的发生过。)
 *
 * ★ 所以输入框一律用这个:合法就给最小单位,不合法给 null,
 *   由调用方决定当成 0 还是「还没填」。校验交给提交那一步。
 */
export function tryFromMajor(major: string, decimals = 2): Minor | null {
  const s = major.trim();
  if (!/^-?\d+(\.\d+)?$/.test(s)) return null;
  return fromMajor(s, decimals);
}

/** 转成展示字符串,不带货币符号 */
export function toMajorString(amount: Minor, decimals = 2): string {
  const neg = amount < 0;
  const abs = Math.abs(amount);
  const unit = 10 ** decimals;
  const intPart = Math.floor(abs / unit);
  const fracPart = String(abs % unit).padStart(decimals, '0');
  return `${neg ? '-' : ''}${intPart}${decimals > 0 ? `.${fracPart}` : ''}`;
}

export function add(...amounts: Minor[]): Minor {
  return minor(amounts.reduce<number>((s, a) => s + a, 0));
}

export function sub(a: Minor, b: Minor): Minor {
  return minor(a - b);
}

/** 乘以数量。数量必须是整数 */
export function mul(amount: Minor, qty: number): Minor {
  if (!Number.isInteger(qty)) throw new TypeError(`数量必须是整数,收到 ${qty}`);
  return minor(amount * qty);
}

/**
 * 按比例计算(手续费、税、折扣)。四舍五入到最小单位。
 * rate 用小数表示,如 0.06 表示 6%。
 */
export function rate(amount: Minor, r: number): Minor {
  return minor(Math.round(amount * r));
}

export function clampMin(amount: Minor, min: Minor): Minor {
  return amount < min ? min : amount;
}

export function isZero(amount: Minor): boolean {
  return amount === 0;
}

/**
 * 把总额按权重分摊到 n 份,保证各份之和精确等于总额。
 * 用于把一笔费用摊到多个订单(如一次取货的打包加成)。
 * 余数按顺序分配给前几份,避免累积误差。
 */
export function allocate(total: Minor, weights: readonly number[]): Minor[] {
  if (weights.length === 0) return [];
  const sum = weights.reduce((s, w) => s + w, 0);
  if (sum <= 0) throw new RangeError('权重之和必须为正');

  const parts = weights.map((w) => Math.floor((total * w) / sum));
  let remainder = total - parts.reduce((s, p) => s + p, 0);
  for (let i = 0; remainder > 0; i = (i + 1) % parts.length, remainder--) {
    parts[i] = (parts[i] ?? 0) + 1;
  }
  return parts.map((p) => minor(p));
}

/** ISO 4217 币种。每种币的小数位不同 —— JPY 是 0 位,不是所有币都是 2 位 */
export const CURRENCY_DECIMALS: Record<string, number> = {
  USD: 2,
  NZD: 2,
  AUD: 2,
  MYR: 2,
  SGD: 2,
  CNY: 2,
  HKD: 2,
  TWD: 2,
  THB: 2,
  IDR: 2,
  PHP: 2,
  VND: 0,
  JPY: 0,
  KRW: 0,
};

export function decimalsOf(currency: string): number {
  return CURRENCY_DECIMALS[currency.toUpperCase()] ?? 2;
}

export function format(amount: Minor, currency: string, locale = 'en'): string {
  const d = decimalsOf(currency);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  }).format(amount / 10 ** d);
}
